import { QuoteStatus } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { QuoteLineItemData } from '../_components/_shared/LineItems/types';
import { quoteAcceptAtom } from '../_graphql/mutationQuoteAccept';
import { quoteConvertAtom } from '../_graphql/mutationQuoteConvert';
import { quoteRejectAtom } from '../_graphql/mutationQuoteReject';
import { quoteRestoreAtom } from '../_graphql/mutationQuoteRestore';
import { quoteRevertToOpenAtom } from '../_graphql/mutationQuoteRevertToOpen';
import { quoteSendAtom } from '../_graphql/mutationQuoteSend';
import { quoteUpdateAtom } from '../_graphql/mutationQuoteUpdate';
import { quoteVoidAtom } from '../_graphql/mutationQuoteVoid';
import { quoteDetailQueryAtom } from '../_graphql/queryQuoteDetail';
import { QuoteDetailRecord } from '../types/quote';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $quoteDetailPathParams = atomFromRoutePath(
  '/brokerage/quotes/:id' as const,
);

export const $quoteTab = atomFromQueryString.string<
  'overview' | 'lineItems' | 'notes'
>('tab');

// 🔘🔘🔘 PLAIN ATOMS
export const $isDeleteDialogOpen = atom(false);
export const $quoteToDelete = atom<
  Array<{ id: string; quoteNumber: string; status: QuoteStatus }>
>([]);

// 🟢🟢🟢 COMPUTED DATA -> quote id from route
export const $quoteId = atom((get) =>
  get($quoteDetailPathParams)?.id
    ? String(get($quoteDetailPathParams)?.id)
    : null,
);

// 🟢🟢🟢 REMOTE DATA -> quote detail
export const $remoteQuoteDetail = quoteDetailQueryAtom((get) => ({
  variables: { id: get($quoteId) ?? '' },
  skip: !get($quoteId),
}));

// 🟢🟢🟢 COMPUTED DATA -> loading state
export const $isQuoteLoading = atom((get) => {
  const data = get($remoteQuoteDetail);
  return data?.isLoading ?? false;
});

// 🟢🟢🟢 COMPUTED DATA -> error state
export const $quoteError = atom((get) => {
  const data = get($remoteQuoteDetail);
  return data?.error ?? null;
});

// 🟠🟠🟠 ACTION -> update quote
export const $updateQuote = quoteUpdateAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> send quote
export const $sendQuote = quoteSendAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> accept quote
export const $acceptQuote = quoteAcceptAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> reject quote
export const $rejectQuote = quoteRejectAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> convert quote
export const $convertQuote = quoteConvertAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> void quote
export const $voidQuote = quoteVoidAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> restore quote
export const $restoreQuote = quoteRestoreAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> revert quote to open
export const $revertToOpen = quoteRevertToOpenAtom({
  onSuccess: (get) => {
    const id = get($quoteId);
    if (id) {
      get($remoteQuoteDetail).refetch?.();
    }
  },
});

// 🟡🟡🟡 LINE ITEMS STATE - Draft Line Items for Quote

const mapQuoteLineItemsToLocal = (
  quoteLineItems: QuoteDetailRecord['lineItems'],
): QuoteLineItemData[] =>
  quoteLineItems.map((li) => ({
    id: li.id,
    itemId: li.itemId,
    item: li.item,
    packageFormat: li.packageFormat,
    quantity: li.quantity,
    unit: li.unit,
    unitPrice: li.unitPrice,
    totalPrice: li.totalPrice,
    notes: li.notes,
  }));

export const $quoteDraftLineItems = atom<QuoteLineItemData[] | null>(null);

export const $quoteEffectiveLineItems = atom((get) => {
  const draft = get($quoteDraftLineItems);
  if (draft !== null) return draft;
  const quoteData = get($remoteQuoteDetail);
  return quoteData?.data?.lineItems
    ? mapQuoteLineItemsToLocal(quoteData.data.lineItems)
    : [];
});

export const $quoteHasLineItemChanges = atom(
  (get) => get($quoteDraftLineItems) !== null,
);
